import { Injectable, NgZone } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { DbService } from './db-service';
import { AuthCookiesService } from './auth-cookies.service';
import { SignalREventType } from '../../enums/signalr-event-type.enum';
import { Customer } from '../../models/customer.model';

@Injectable({
    providedIn: 'root',
})
export class SignalRService {
    private readonly signalrApi = environment.signalrApi;
    private hubConnection: signalR.HubConnection | null = null;
    private connected = false;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private eventStreams = new Map<SignalREventType, Subject<any>>();

    constructor(
        private readonly dbService: DbService,
        private readonly cookieService: AuthCookiesService,
        private readonly ngZone: NgZone
    ) {}

    public startConnection(): void {
        if (this.connected) return;

        const token = this.cookieService.getIdTokenFromCookie();
        if (!token) return;

        this.hubConnection = new signalR.HubConnectionBuilder()
            .withUrl(`${this.signalrApi}/hubs/notifications?clientId=${this.dbService.selectedClientId}`, {
                accessTokenFactory: () => token,
            })
            .withAutomaticReconnect()
            .configureLogging(signalR.LogLevel.Information)
            .build();

        this.hubConnection.on('ReceiveMessage', (eventType: SignalREventType, payload: Customer) => {
            // The signalr client's own transport callbacks run outside Angular's
            // zone, so without this, subscribers mutate state correctly but no
            // change detection ever runs - the view just sits stale until some
            // unrelated interaction happens to trigger a CD cycle.
            this.ngZone.run(() => {
                const subject = this.eventStreams.get(eventType);
                if (subject) {
                    subject.next(payload);
                }
            });
        });

        this.hubConnection.onclose(async () => {
            this.connected = false;

            while (true) {
                try {
                    await this.hubConnection?.start();
                    console.log('SignalR reconnected!');
                    this.connected = true;
                    break;
                } catch (err) {
                    console.warn('Reconnect failed, retrying in 5s...', err);
                    await new Promise((res) => setTimeout(res, 5000));
                }
            }
        });

        this.hubConnection
            .start()
            .then(() => {
                this.connected = true;
            })
            .catch((err) => console.error('SignalR connection error:', err));
    }

    public onEvent<T>(eventType: SignalREventType): Observable<T> {
        if (!this.eventStreams.has(eventType)) {
            this.eventStreams.set(eventType, new Subject<T>());
        }
        return this.eventStreams.get(eventType)!.asObservable();
    }

    public stopConnection(restart = false): void {
        if (this.hubConnection) {
            this.hubConnection.stop().then(() => {
                this.connected = false;
                if (restart) {
                    this.startConnection();
                }
            });
            this.hubConnection = null;
        }
    }
}
